import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Upload,
  Button,
  Space,
  Tag,
  Progress,
  Modal,
  message,
  Statistic,
  Alert,
  Tooltip,
  Spin
} from 'antd';
import {
  UploadOutlined,
  FileTextOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  CloudUploadOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useAuth } from '../../hooks/useAuth';
import {
  useGetKnowledgeDocumentsQuery,
  useGetDocumentStatsQuery,
  useUploadKnowledgeDocumentMutation,
  useDeleteKnowledgeDocumentMutation,
  IKnowledgeDocument
} from '../../services/nestjs-base-api';
import { ResponsiveTable } from '../components/ResponsiveTable';

const { Title, Text } = Typography;
const { Dragger } = Upload;

export const AIExpertsDocumentsPage: React.FC = () => {
  const { user } = useAuth();
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});

  // Fetch real data from API
  const {
    data: documentsData,
    isLoading: documentsLoading,
    error: documentsError
  } = useGetKnowledgeDocumentsQuery({
    jwtToken: user?.jwtToken || '',
    page: 1,
    limit: 20
  });

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError
  } = useGetDocumentStatsQuery({
    jwtToken: user?.jwtToken || '',
  });

  const [uploadDocument] = useUploadKnowledgeDocumentMutation();
  const [deleteDocument] = useDeleteKnowledgeDocumentMutation();

  const documents = documentsData?.documents || [];
  const loading = documentsLoading || statsLoading;

  if (documentsError || statsError) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Error loading documents data"
          description="Unable to fetch documents or statistics. Please try again later."
          type="error"
          showIcon
        />
      </div>
    );
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'processing';
      case 'failed': return 'error';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf': return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
      case 'xlsx': return <FileExcelOutlined style={{ color: '#52c41a' }} />;
      default: return <FileTextOutlined />;
    }
  };

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onProgress, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append('file', file as File);

    try {
      const uploadId = Date.now().toString();
      setUploadProgress(prev => ({ ...prev, [uploadId]: 0 }));
      
      // Simulate progress for UI feedback
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          const currentProgress = prev[uploadId] || 0;
          const newProgress = Math.min(currentProgress + 20, 100);
          if (newProgress === 100) {
            clearInterval(interval);
            setTimeout(() => {
              setUploadProgress(prev => {
                const { [uploadId]: _, ...rest } = prev;
                return rest;
              });
            }, 1000);
          }
          return { ...prev, [uploadId]: newProgress };
        });
      }, 500);

      // Real API call
      const result = await uploadDocument({
        jwtToken: user?.jwtToken || '',
        file: formData
      }).unwrap();

      message.success(`${(file as File).name} uploaded successfully!`);
      onSuccess?.(result);

    } catch (error: any) {
      console.error('Upload failed:', error);
      message.error(error?.data?.message || 'Upload failed. Please try again.');
      onError?.(error);
    }
  };

  const handleDelete = (documentId: string, filename: string) => {
    Modal.confirm({
      title: 'Delete Document',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${filename}"? This action cannot be undone and will remove all associated chunks from the knowledge base.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteDocument({
            jwtToken: user?.jwtToken || '',
            documentId
          }).unwrap();
          message.success('Document deleted successfully');
        } catch (error: any) {
          message.error(error?.data?.message || 'Failed to delete document');
        }
      },
    });
  };


  const columns = [
    {
      title: 'File',
      dataIndex: 'filename',
      key: 'filename',
      render: (filename: string, record: IKnowledgeDocument) => (
        <Space>
          {getFileIcon(record.fileType)}
          <Text strong>{filename}</Text>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'fileType',
      key: 'fileType',
      render: (type: string) => (
        <Tag color={type === 'pdf' ? 'red' : 'green'}>
          {type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size: number) => formatFileSize(size),
    },
    {
      title: 'Status',
      dataIndex: 'processingStatus',
      key: 'processingStatus',
      render: (status: string, record: IKnowledgeDocument) => (
        <Space direction="vertical" size={2}>
          <Tag color={getStatusColor(status)}>
            {status.toUpperCase()}
            {status === 'processing' && <Spin size="small" style={{ marginLeft: 8 }} />}
          </Tag>
          {record.errorMessage && (
            <Tooltip title={record.errorMessage}>
              <Text type="danger" style={{ fontSize: '12px' }}>
                Error: {record.errorMessage.substring(0, 30)}...
              </Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Chunks',
      dataIndex: 'chunkCount',
      key: 'chunkCount',
      render: (count: number) => (
        <Text>{count > 0 ? count : '-'}</Text>
      ),
    },
    {
      title: 'Uploaded',
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Processed',
      dataIndex: 'processedDate',
      key: 'processedDate',
      render: (date?: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Uploader',
      dataIndex: ['uploader', 'email'],
      key: 'uploader',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: IKnowledgeDocument) => (
        <Space>
          <Tooltip title="Delete document">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id, record.filename)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    accept: '.pdf,.xlsx',
    customRequest: handleUpload,
    showUploadList: false,
    beforeUpload: (file) => {
      const isPDF = file.type === 'application/pdf';
      const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      if (!isPDF && !isXLSX) {
        message.error('Only PDF and XLSX files are allowed!');
        return false;
      }
      
      const isLt50M = file.size / 1024 / 1024 < 50;
      if (!isLt50M) {
        message.error('File must be smaller than 50MB!');
        return false;
      }
      
      return true;
    },
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 24 }}>
        Knowledge Base Documents
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Documents"
              value={stats?.totalDocuments || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#F59E0B' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Knowledge Chunks"
              value={stats?.totalChunks || 0}
              prefix={<CloudUploadOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Processing"
              value={stats?.processingDocuments || 0}
              prefix={<ReloadOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Storage Used"
              value={formatFileSize(stats?.totalSizeBytes || 0)}
              prefix={<CloudUploadOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Upload Section */}
      <Card title="Upload Knowledge Documents" style={{ marginBottom: 24 }}>
        <Alert
          message="Supported file types: PDF and XLSX files up to 50MB each"
          description="Documents will be automatically processed and chunked for the AI knowledge base. Processing typically takes 1-3 minutes depending on file size."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Dragger {...uploadProps} style={{ padding: '20px' }}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: '48px', color: '#F59E0B' }} />
          </p>
          <p className="ant-upload-text">
            Click or drag files here to upload
          </p>
          <p className="ant-upload-hint">
            Support for PDF and XLSX files. Multiple files can be uploaded at once.
          </p>
        </Dragger>

        {/* Upload Progress */}
        {Object.entries(uploadProgress).length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Upload Progress</Title>
            {Object.entries(uploadProgress).map(([id, progress]) => (
              <div key={id} style={{ marginBottom: 8 }}>
                <Progress percent={progress} status={progress < 100 ? 'active' : 'success'} />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Documents Table */}
      <Card title="Knowledge Documents">
        <ResponsiveTable
          dataSource={documents}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} documents`,
          }}
          loading={loading}
        />
      </Card>
    </div>
  );
};